import type {
  ScaffolderApi,
  ScaffolderTask,
} from '@backstage/plugin-scaffolder-react';
import type { AppTypeDetail } from '../shared/appTypes';
import {
  GENERIC_TEMPLATES,
  ODOO_CREATE_TEMPLATE,
  roleOfTemplate,
} from '../shared/templateRoles';

/**
 * The templates of each role the embedded Odoo descriptor designates
 * (`shared/templateRoles.ts`, `EMBEDDED_ODOO_TEMPLATES`): the Odoo creation
 * and the generic templates of step 7 — the refs the Odoo wizard launches
 * (creation) and the tests start their tasks from. A task is recognised by
 * its template's ROLE, so another type's templates get the same pages.
 */
export const PROJECT_DELETE_TEMPLATE = GENERIC_TEMPLATES.delete;
export const PROJECT_CREATE_TEMPLATE = ODOO_CREATE_TEMPLATE;
/**
 * Waking and putting to sleep one permanent environment of a project
 * (`projectEnvironments/environmentPower.ts`).
 */
export const ENV_WAKE_TEMPLATE = GENERIC_TEMPLATES.wake;
export const ENV_SLEEP_TEMPLATE = GENERIC_TEMPLATES.sleep;
/**
 * Promoting a permanent branch to the next one
 * (`projectEnvironments/promotion.ts`).
 */
export const ENV_PROMOTE_TEMPLATE = GENERIC_TEMPLATES.promote;
/**
 * Changing the configuration of a permanent environment
 * (`projectEnvironments/configuration.ts`).
 */
export const ENV_CONFIGURE_TEMPLATE = GENERIC_TEMPLATES.configure;

/** What a task does to its project. */
export type TaskProjectAction =
  | 'deletion'
  | 'simulation'
  | 'creation'
  | 'wake'
  | 'sleep'
  | 'promote'
  | 'configure'
  | 'restore';

export interface TaskProject {
  project: string;
  action: TaskProjectAction;
  /**
   * The permanent environment of a wake, a sleep, a change of
   * configuration or a point-in-time restore, a promotion's target.
   */
  environment?: string;
  /** A promotion's source branch. */
  source?: string;
}

/**
 * The project a task is about, read from the parameters it was started with,
 * by the role of its template (`roleOfTemplate`, with the descriptors
 * `appTypes` for a template only an AppType designates): `project` (and `dryRun`)
 * for a deletion, `projectName` for a creation, `project` and `environment`
 * for a wake, a sleep and a change of configuration, `project`, `source` and
 * `target` for a promotion.
 * Undefined for any other template, or when a parameter is missing.
 *
 * `templateInfo.entityRef` is written by the scaffolder backend with
 * `stringifyEntityRef`, which lowercases it; `roleOfTemplate` compares it
 * lower-cased too, as the backend's template locks do.
 *
 * A deletion is a simulation unless `dryRun` is exactly `false` — the reading
 * of the deletion actions themselves (`isDryRun`, `projectDelete/common.ts`),
 * so the banner never says « suppression réelle » for a run that simulated.
 */
export function taskProject(
  task: ScaffolderTask,
  appTypes: readonly AppTypeDetail[] = [],
): TaskProject | undefined {
  const role = roleOfTemplate(task.spec.templateInfo?.entityRef, appTypes);
  const parameters = task.spec.parameters;

  if (role === 'delete') {
    const project = parameters.project;
    if (typeof project !== 'string' || project === '') return undefined;
    return {
      project,
      action: parameters.dryRun === false ? 'deletion' : 'simulation',
    };
  }
  if (role === 'create') {
    const project = parameters.projectName;
    if (typeof project !== 'string' || project === '') return undefined;
    return { project, action: 'creation' };
  }
  if (
    role === 'wake' ||
    role === 'sleep' ||
    role === 'configure' ||
    role === 'restore'
  ) {
    const { project, environment } = parameters;
    if (
      typeof project !== 'string' ||
      project === '' ||
      typeof environment !== 'string' ||
      environment === ''
    ) {
      return undefined;
    }
    return {
      project,
      environment,
      action: role,
    };
  }
  if (role === 'promote') {
    const { project, source, target } = parameters;
    if (
      typeof project !== 'string' ||
      project === '' ||
      typeof source !== 'string' ||
      source === '' ||
      typeof target !== 'string' ||
      target === ''
    ) {
      return undefined;
    }
    return { project, source, environment: target, action: 'promote' };
  }
  return undefined;
}

/**
 * One `getTask` per task id. A task's template and parameters never change
 * once it is created, so the answer is kept for the page's lifetime. What is
 * kept is the promise, so a second mount while the request is in flight
 * shares it; a rejected one is dropped, and the next mount retries.
 *
 * The cache hangs off the app's `ScaffolderApi` instance, as `useIsAdmin.ts`
 * does with `FetchApi`: one cache for the app, a fresh one per test.
 */
const tasks = new WeakMap<
  ScaffolderApi,
  Map<string, Promise<ScaffolderTask>>
>();

export function loadTask(
  scaffolderApi: ScaffolderApi,
  taskId: string,
): Promise<ScaffolderTask> {
  const byId =
    tasks.get(scaffolderApi) ?? new Map<string, Promise<ScaffolderTask>>();
  tasks.set(scaffolderApi, byId);
  const cached = byId.get(taskId);
  if (cached) return cached;

  const promise = scaffolderApi.getTask(taskId);
  byId.set(taskId, promise);
  // The caller handles the rejection; this branch only forgets it, and
  // cannot reject itself.
  void promise.catch(() => byId.delete(taskId));
  return promise;
}
